import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  TreePine, RefreshCw, Play, Settings, Search,
  Loader2, CheckCircle, XCircle, Clock, AlertTriangle, Activity, MapPin, Zap
} from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import ModuleHeader from "@/components/ModuleHeader";

function formatNumber(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}

function formatDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function formatDuration(s: number | null | undefined) {
  if (!s) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function formatEta(elapsedSec: number, progress: number, total: number): string {
  if (!progress || !total || progress >= total) return "—";
  const rate = progress / elapsedSec;
  const remaining = (total - progress) / rate;
  if (!isFinite(remaining)) return "—";
  const m = Math.floor(remaining / 60);
  const s = Math.round(remaining % 60);
  if (m > 60) return `~${Math.round(m / 60)}h`;
  return m > 0 ? `~${m}m ${s}s` : `~${s}s`;
}

interface LogEntry {
  ts: number;
  msg: string;
}

const FASES = ["Declarada", "Delimitada", "Homologada", "Regularizada", "Em estudo"];
const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

export default function FunaiPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Sync monitor state
  const [syncActive, setSyncActive] = useState(false);
  const [logLines, setLogLines] = useState<LogEntry[]>([]);
  const [startTime, setStartTime] = useState<number>(0);
  const [elapsed, setElapsed] = useState(0);
  const logRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Overlap query state
  const [latPonto, setLatPonto] = useState("");
  const [lonPonto, setLonPonto] = useState("");
  const [pontoResult, setPontoResult] = useState<any>(null);
  const [pontoLoading, setPontoLoading] = useState(false);

  // Terra listing state
  const [filtroUf, setFiltroUf] = useState("");
  const [filtroFase, setFiltroFase] = useState("");
  const [filtroBusca, setFiltroBusca] = useState("");
  const [terraPage, setTerraPage] = useState(0);
  const TERRA_LIMIT = 50;

  // Radius search state
  const [latRaio, setLatRaio] = useState("");
  const [lonRaio, setLonRaio] = useState("");
  const [raio, setRaio] = useState("100");
  const [raioResult, setRaioResult] = useState<any>(null);
  const [raioLoading, setRaioLoading] = useState(false);

  // Config
  const [cfgEnabled, setCfgEnabled] = useState(true);
  const [cfgDays, setCfgDays] = useState("30");

  const { data: status, refetch: refetchStatus, isFetching: statusFetching } = useQuery({
    queryKey: ["/api/funai/status"],
    queryFn: () => authFetch("/api/funai/status"),
    refetchInterval: syncActive ? 1000 : 30000,
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["/api/funai/config"],
    queryFn: () => authFetch("/api/funai/config"),
  });

  const { data: syncLog } = useQuery({
    queryKey: ["/api/funai/sync-log"],
    queryFn: () => authFetch("/api/funai/sync-log"),
    refetchInterval: syncActive ? 5000 : false,
  });

  const { data: terrasData, isLoading: terrasLoading } = useQuery({
    queryKey: ["/api/funai/terras", filtroUf, filtroFase, filtroBusca, terraPage],
    queryFn: () =>
      authFetch(
        `/api/funai/terras?uf=${filtroUf}&fase=${filtroFase}&busca=${encodeURIComponent(filtroBusca)}&limit=${TERRA_LIMIT}&offset=${terraPage * TERRA_LIMIT}`
      ),
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () =>
      authFetch("/api/funai/sync", { method: "POST" }),
    onSuccess: (data: any) => {
      toast({ title: "Sync iniciado", description: data.message });
      setSyncActive(true);
      setStartTime(Date.now());
      setLogLines([{ ts: Date.now(), msg: "Sync iniciado..." }]);
      queryClient.invalidateQueries({ queryKey: ["/api/funai/status"] });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message || "Falha ao iniciar sync", variant: "destructive" });
    },
  });

  const configMutation = useMutation({
    mutationFn: (cfg: { enabled: boolean; intervalDays: number }) =>
      authFetch("/api/funai/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cfg),
      }),
    onSuccess: () => {
      toast({ title: "Configuração salva" });
      queryClient.invalidateQueries({ queryKey: ["/api/funai/config"] });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  // Init config form from fetched data
  useEffect(() => {
    if (config) {
      setCfgEnabled(config.enabled ?? true);
      setCfgDays(String(config.intervalDays ?? 30));
    }
  }, [config]);

  // Polling for live log
  useEffect(() => {
    if (!syncActive) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await authFetch("/api/funai/status");
        if (s.sync_log) {
          setLogLines(prev => {
            const last = prev[prev.length - 1];
            if (!last || last.msg !== s.sync_log) {
              return [...prev, { ts: Date.now(), msg: s.sync_log }];
            }
            return prev;
          });
        }
        if (!s.sync_ativo) {
          setSyncActive(false);
          queryClient.invalidateQueries({ queryKey: ["/api/funai/status"] });
          queryClient.invalidateQueries({ queryKey: ["/api/funai/sync-log"] });
        }
      } catch {}
    }, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncActive, queryClient]);

  // Elapsed timer
  useEffect(() => {
    if (!syncActive || !startTime) return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - startTime) / 1000)), 500);
    return () => clearInterval(t);
  }, [syncActive, startTime]);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLines]);

  // Detect running sync on load
  useEffect(() => {
    if (status?.sync_ativo && !syncActive) {
      setSyncActive(true);
      setStartTime(Date.now());
      setLogLines([{ ts: Date.now(), msg: status.sync_log || "Sync em andamento..." }]);
    }
  }, [status?.sync_ativo]);

  const ti = status?.tabelas?.funai_terras_indigenas;
  const prog = status?.sync_progresso ?? 0;
  const tot = status?.sync_total ?? 0;
  const pct = tot > 0 ? Math.round((prog / tot) * 100) : 0;
  const rate = elapsed > 0 ? (prog / elapsed).toFixed(1) : "—";

  async function handlePontoSearch() {
    const lat = parseFloat(latPonto);
    const lon = parseFloat(lonPonto);
    if (isNaN(lat) || isNaN(lon)) {
      toast({ title: "Erro", description: "Informe lat e lon válidos", variant: "destructive" });
      return;
    }
    setPontoLoading(true);
    try {
      const res = await authFetch(`/api/funai/sobreposicao?lat=${lat}&lon=${lon}`);
      setPontoResult(res);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setPontoLoading(false);
    }
  }

  async function handleRaioSearch() {
    const lat = parseFloat(latRaio);
    const lon = parseFloat(lonRaio);
    const km = parseFloat(raio);
    if (isNaN(lat) || isNaN(lon)) {
      toast({ title: "Erro", description: "Informe lat e lon válidos", variant: "destructive" });
      return;
    }
    setRaioLoading(true);
    try {
      const res = await authFetch(`/api/funai/terras/raio?lat=${lat}&lon=${lon}&raio=${km}`);
      setRaioResult(res);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setRaioLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <ModuleHeader
        icon={TreePine}
        iconColor="text-green-500"
        title="FUNAI — Terras Indígenas"
        description="SRID 4326 · Fonte: GeoServer FUNAI"
        recordCount={ti?.total}
        recordLabel="TIs"
        onRefresh={() => refetchStatus()}
        refreshing={statusFetching}
        isLoading={!ti}
        docsHref="/docs"
        extra={
          <>
            {syncActive && (
              <Badge variant="default" className="animate-pulse bg-green-500 text-white gap-1">
                <Activity className="h-3 w-3" /> AO VIVO
              </Badge>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending || syncActive}
              data-testid="button-sync-funai"
            >
              {syncMutation.isPending || syncActive ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-1" />
              )}
              Importar TIs
            </Button>
          </>
        }
      />

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Terras Indígenas</p>
            <p className="text-3xl font-bold text-green-500 mt-1" data-testid="stat-funai-total">{formatNumber(ti?.total)}</p>
            <p className="text-xs text-muted-foreground mt-1">polígonos importados</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Última Sync</p>
            <p className="text-sm font-semibold mt-1">{formatDate(ti?.ultima_sync)}</p>
            <p className="text-xs text-muted-foreground mt-1">duração: {formatDuration(ti?.duracao_seg)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Próxima Sync</p>
            <p className="text-sm font-semibold mt-1">{formatDate(status?.proxima_sync)}</p>
            <p className="text-xs text-muted-foreground mt-1">{status?.config?.intervalDays ?? 30}d intervalo</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Status</p>
            {ti?.erro ? (
              <div className="flex items-center gap-1 mt-1 text-red-500">
                <XCircle className="h-4 w-4" />
                <span className="text-sm font-semibold">Erro</span>
              </div>
            ) : ti?.total > 0 ? (
              <div className="flex items-center gap-1 mt-1 text-green-500">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-semibold">OK</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 mt-1 text-yellow-500">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-semibold">Aguardando</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live Monitor */}
      {syncActive && (
        <Card className="border-green-500/40 bg-green-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-green-500" />
              Monitor ao Vivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-mono font-semibold">{pct}% ({formatNumber(prog)}/{formatNumber(tot)})</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="flex gap-6 text-xs text-muted-foreground">
              <span>⏱ {elapsed}s</span>
              <span>🚀 {rate} reg/s</span>
              <span>⌛ ETA: {formatEta(elapsed, prog, tot)}</span>
            </div>
            <div
              ref={logRef}
              className="bg-black/60 rounded font-mono text-xs text-green-400 p-3 h-32 overflow-y-auto"
            >
              {logLines.map((l, i) => (
                <div key={i} className="whitespace-pre-wrap leading-5">
                  <span className="text-green-600">[{new Date(l.ts).toLocaleTimeString("pt-BR")}]</span>{" "}
                  {l.msg}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="sobreposicao">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="sobreposicao">Sobreposição</TabsTrigger>
          <TabsTrigger value="raio">Por Raio</TabsTrigger>
          <TabsTrigger value="terras">Terras</TabsTrigger>
          <TabsTrigger value="config">Config & Log</TabsTrigger>
        </TabsList>

        {/* ─── Sobreposição por ponto ─── */}
        <TabsContent value="sobreposicao">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-green-500" />
                Sobreposição — Ponto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Latitude</Label>
                  <Input
                    placeholder="-3.0456"
                    value={latPonto}
                    onChange={e => setLatPonto(e.target.value)}
                    data-testid="input-funai-lat"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Longitude</Label>
                  <Input
                    placeholder="-60.1234"
                    value={lonPonto}
                    onChange={e => setLonPonto(e.target.value)}
                    data-testid="input-funai-lon"
                  />
                </div>
              </div>
              <Button onClick={handlePontoSearch} disabled={pontoLoading} data-testid="button-funai-ponto">
                {pontoLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Verificar Sobreposição
              </Button>

              {pontoResult && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2">
                    {pontoResult.dentro_ti ? (
                      <Badge className="bg-red-500 text-white text-sm">DENTRO de Terra Indígena</Badge>
                    ) : (
                      <Badge className="bg-green-500 text-white text-sm">Fora de Terra Indígena</Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {pontoResult.total} TI(s) encontrada(s)
                    </span>
                  </div>
                  {pontoResult.terras?.map((t: any) => (
                    <Card key={t.id} className="border-red-500/30">
                      <CardContent className="pt-3 pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold" data-testid={`text-ti-nome-${t.id}`}>{t.nome_ti}</p>
                            <p className="text-sm text-muted-foreground">{t.etnia} · {t.municipio}/{t.uf}</p>
                          </div>
                          <Badge variant="outline">{t.fase_ti}</Badge>
                        </div>
                        <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                          <span>{formatNumber(t.superficie_ha)} ha</span>
                          <span>{t.modalidade}</span>
                          {t.faixa_fronteira === "Sim" && (
                            <span className="text-yellow-500">⚠ Faixa de Fronteira</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Busca por raio ─── */}
        <TabsContent value="raio">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-4 w-4 text-green-500" />
                Terras Indígenas por Raio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Latitude</Label>
                  <Input
                    placeholder="-3.0456"
                    value={latRaio}
                    onChange={e => setLatRaio(e.target.value)}
                    data-testid="input-funai-raio-lat"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Longitude</Label>
                  <Input
                    placeholder="-60.1234"
                    value={lonRaio}
                    onChange={e => setLonRaio(e.target.value)}
                    data-testid="input-funai-raio-lon"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Raio (km)</Label>
                  <Input
                    placeholder="100"
                    value={raio}
                    onChange={e => setRaio(e.target.value)}
                    data-testid="input-funai-raio-km"
                  />
                </div>
              </div>
              <Button onClick={handleRaioSearch} disabled={raioLoading} data-testid="button-funai-raio">
                {raioLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Buscar no Raio
              </Button>

              {raioResult && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm text-muted-foreground">
                    {raioResult.total} TIs num raio de {raioResult.raio_km} km
                  </p>
                  {raioResult.data?.map((t: any) => (
                    <div key={t.id} className="border rounded p-3 flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-sm" data-testid={`text-ti-raio-${t.id}`}>{t.nome_ti}</p>
                        <p className="text-xs text-muted-foreground">{t.etnia} · {t.municipio}/{t.uf}</p>
                      </div>
                      <div className="text-right text-xs">
                        <Badge variant="outline" className="mb-1">{t.fase_ti}</Badge>
                        <p className="text-muted-foreground">{t.distancia_km} km</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Lista de Terras ─── */}
        <TabsContent value="terras">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Terras Indígenas ({formatNumber(terrasData?.total)} total)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>UF</Label>
                  <select
                    className="w-full rounded border bg-background text-sm px-2 py-1.5"
                    value={filtroUf}
                    onChange={e => { setFiltroUf(e.target.value); setTerraPage(0); }}
                    data-testid="select-funai-uf"
                  >
                    <option value="">Todas</option>
                    {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Fase</Label>
                  <select
                    className="w-full rounded border bg-background text-sm px-2 py-1.5"
                    value={filtroFase}
                    onChange={e => { setFiltroFase(e.target.value); setTerraPage(0); }}
                    data-testid="select-funai-fase"
                  >
                    <option value="">Todas</option>
                    {FASES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Busca</Label>
                  <Input
                    placeholder="Nome ou etnia..."
                    value={filtroBusca}
                    onChange={e => { setFiltroBusca(e.target.value); setTerraPage(0); }}
                    data-testid="input-funai-busca"
                  />
                </div>
              </div>

              {terrasLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-1 max-h-[480px] overflow-y-auto">
                  {terrasData?.data?.map((t: any) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between text-sm border rounded px-3 py-2 hover:bg-muted/50 transition-colors"
                      data-testid={`row-ti-${t.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{t.nome_ti}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {t.etnia} · {t.municipio}/{t.uf}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 ml-3 shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {formatNumber(Math.round(t.superficie_ha ?? 0))} ha
                        </span>
                        <Badge
                          variant="outline"
                          className={
                            t.fase_ti === "Regularizada" ? "border-green-500 text-green-600" :
                            t.fase_ti === "Homologada" ? "border-blue-500 text-blue-600" :
                            t.fase_ti === "Declarada" ? "border-yellow-500 text-yellow-600" :
                            ""
                          }
                        >
                          {t.fase_ti}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={terraPage === 0}
                  onClick={() => setTerraPage(p => p - 1)}
                  data-testid="button-funai-prev"
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Pág {terraPage + 1} · {formatNumber(terrasData?.total)} registros
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!terrasData || (terraPage + 1) * TERRA_LIMIT >= (terrasData?.total ?? 0)}
                  onClick={() => setTerraPage(p => p + 1)}
                  data-testid="button-funai-next"
                >
                  Próxima
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Config & Log ─── */}
        <TabsContent value="config">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Config */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-4 w-4" /> Scheduler
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {configLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Sync automático</Label>
                        <p className="text-xs text-muted-foreground">Atualiza periodicamente</p>
                      </div>
                      <Switch
                        checked={cfgEnabled}
                        onCheckedChange={setCfgEnabled}
                        data-testid="switch-funai-enabled"
                      />
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label>Intervalo (dias)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={cfgDays}
                        onChange={e => setCfgDays(e.target.value)}
                        data-testid="input-funai-interval"
                      />
                    </div>
                    <Button
                      onClick={() =>
                        configMutation.mutate({
                          enabled: cfgEnabled,
                          intervalDays: parseInt(cfgDays) || 30,
                        })
                      }
                      disabled={configMutation.isPending}
                      data-testid="button-funai-save-config"
                    >
                      {configMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Salvar
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Sync Log */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Histórico de Importações
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {syncLog?.data?.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">Nenhuma importação registrada</p>
                  )}
                  {syncLog?.data?.map((l: any) => (
                    <div key={l.id} className="border rounded px-3 py-2 text-sm" data-testid={`row-log-${l.id}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{formatDate(l.created_at)}</span>
                        {l.erro ? (
                          <Badge variant="destructive" className="text-xs">Erro</Badge>
                        ) : (
                          <Badge className="bg-green-500 text-white text-xs">OK</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex gap-3">
                        <span>{formatNumber(l.registros_importados)} TIs</span>
                        <span>{formatDuration(l.duracao_seg)}</span>
                        {l.erro && <span className="text-red-400">{l.erro}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
